import { useEffect, useRef, useState, type DragEvent } from "react";
import { GripVertical, ImagePlus, Plus, Trash2, X } from "lucide-react";
import { useDraftStore } from "@/shared/store/draft";
import type { CreativeVariant } from "@/shared/store/draft";
import { useUploadMedia } from "@/shared/api/wb";
import { Card } from "@/shared/ui";
import { cn } from "@/shared/lib/cn";
import { StepHead } from "../StepHead";
import { WizardLayout } from "../WizardLayout";
import { CreativeVariantsEditor, SEED_VARIANT } from "../CreativeVariantsEditor";

const NATIVE_SEED: CreativeVariant = {
  id: "nb1",
  name: "Бриф А",
  text: "",
  tone: "",
  forbidden: "",
  link: "",
  media: [],
};

function makeNativeBrief(index: number): CreativeVariant {
  const letter = String.fromCharCode(65 + index);
  return { ...NATIVE_SEED, id: `nb${Date.now()}_${index}`, name: `Бриф ${letter}` };
}

export function CreativeStep() {
  const { branch, draft, setVariants } = useDraftStore();
  const isSeeding = branch.campaignType === "seeding";
  const variants = draft.variants ?? [];

  useEffect(() => {
    // Re-initialize when the current variants don't match the active flow.
    // Seeding variants have IDs starting with "cv"; native briefs with "nb".
    const firstId = variants[0]?.id ?? "";
    const variantsAreNative = firstId.startsWith("nb");
    const variantsAreSeeding = !variantsAreNative && variants.length > 0;

    if (isSeeding) {
      if (variants.length === 0 || variantsAreNative) {
        setVariants([{ ...SEED_VARIANT, link: draft.productUrl || "" }]);
      }
    } else {
      if (variants.length === 0 || variantsAreSeeding) {
        setVariants([{ ...NATIVE_SEED }]);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSeeding]);

  return (
    <WizardLayout>
      {isSeeding ? (
        <StepHead
          step="03"
          title="Креатив"
          sub="AI собрал черновики под продукт. Редактируйте, добавляйте варианты для A/B и привязывайте к каналам."
        />
      ) : (
        <StepHead
          step="03"
          title="ТЗ блогеру"
          sub="Создайте один или несколько вариантов брифа для A/B — блогер напишет пост по выбранному."
        />
      )}

      {isSeeding ? (
        <CreativeVariantsEditor
          variants={variants}
          onChange={setVariants}
          autoGenerateOnEmpty
        />
      ) : (
        <NativeVariantsEditor
          variants={variants}
          onChange={setVariants}
        />
      )}
    </WizardLayout>
  );
}

// ─── Native variants editor ────────────────────────────────────────────────

function NativeVariantsEditor({
  variants,
  onChange,
}: {
  variants: CreativeVariant[];
  onChange: (vs: CreativeVariant[]) => void;
}) {
  const [activeId, setActiveId] = useState<string>(() => variants[0]?.id ?? "");

  const safeVariants = variants.length > 0 ? variants : [{ ...NATIVE_SEED }];
  const active = safeVariants.find((v) => v.id === activeId) ?? safeVariants[0];

  function patch(id: string, p: Partial<CreativeVariant>) {
    onChange(safeVariants.map((v) => (v.id === id ? { ...v, ...p } : v)));
  }

  function addVariant() {
    if (safeVariants.length >= 5) return;
    const next = makeNativeBrief(safeVariants.length);
    onChange([...safeVariants, next]);
    setActiveId(next.id);
  }

  function removeVariant(id: string) {
    if (safeVariants.length <= 1) return;
    const next = safeVariants.filter((v) => v.id !== id);
    onChange(next);
    if (activeId === id) setActiveId(next[0].id);
  }

  return (
    <div className="mt-5 grid gap-4">
      {/* Tab bar */}
      <div className="flex items-center gap-1.5 flex-wrap">
        {safeVariants.map((v) => (
          <div key={v.id} className="relative group flex items-center">
            <button
              onClick={() => setActiveId(v.id)}
              className={cn(
                "h-8 px-3.5 rounded-pill border font-display font-semibold text-[13px] transition-colors pr-7",
                v.id === active.id
                  ? "bg-ink text-white border-ink"
                  : "bg-white border-ink-12 text-ink-60 hover:border-ink/40 hover:text-ink",
              )}
            >
              {v.name}
            </button>
            {safeVariants.length > 1 && (
              <button
                onClick={(e) => { e.stopPropagation(); removeVariant(v.id); }}
                title="Удалить вариант"
                className={cn(
                  "absolute right-1 w-5 h-5 grid place-items-center rounded-full text-[10px]",
                  v.id === active.id
                    ? "text-white/60 hover:text-white hover:bg-white/20"
                    : "text-ink-30 hover:text-pink hover:bg-pink/10",
                )}
              >
                <X size={10} strokeWidth={2.5} />
              </button>
            )}
          </div>
        ))}
        {safeVariants.length < 5 && (
          <button
            onClick={addVariant}
            className="h-8 px-3 rounded-pill border border-dashed border-ink-20 text-[12.5px] font-display font-semibold text-ink-40 hover:text-ink hover:border-ink/60 transition-colors inline-flex items-center gap-1"
          >
            <Plus size={12} strokeWidth={2.5} /> Добавить вариант
          </button>
        )}
      </div>

      {/* Active brief card */}
      <Card className="!p-6 grid gap-5">
        <BriefNameField
          value={active.name}
          onChange={(v) => patch(active.id, { name: v })}
        />

        <BriefSection label="Что нужно сделать" hint="Тема поста, ключевые сообщения, что хотите донести">
          <BriefTextarea
            value={active.text}
            onChange={(v) => patch(active.id, { text: v })}
            rows={4}
            placeholder="Расскажи о нашем приложении — упор на простоту регистрации и бонус за первый заказ…"
          />
        </BriefSection>

        <BriefSection label="Тон и стиль">
          <BriefTextarea
            value={active.tone}
            onChange={(v) => patch(active.id, { tone: v })}
            rows={2}
            placeholder="Живой и непринуждённый, без канцелярита, с эмодзи…"
          />
        </BriefSection>

        <BriefSection label="Что нельзя упоминать">
          <BriefTextarea
            value={active.forbidden ?? ""}
            onChange={(v) => patch(active.id, { forbidden: v })}
            rows={2}
            placeholder="Не упоминать конкурентов, не использовать слово «дешёвый»…"
          />
        </BriefSection>

        <BriefMediaBlock
          media={active.media ?? []}
          onChange={(m) => patch(active.id, { media: m })}
        />

        <BriefSection label="Ссылка для блогера" hint="Куда вести аудиторию">
          <BriefInput
            value={active.link}
            onChange={(v) => patch(active.id, { link: v })}
            placeholder="https://example.com/landing"
            type="url"
          />
        </BriefSection>

        <div className="p-3.5 rounded-md bg-ink-6 text-[12.5px] text-ink-60 leading-relaxed">
          UTM-метки для отслеживания создаются автоматически для каждого канала при запуске — посмотреть и поправить можно будет в карточке размещения на канбане кампании.
        </div>

        <div className="p-3.5 rounded-md bg-ink-6 text-[12.5px] text-ink-60 leading-relaxed">
          ТЗ будет передано каждому блогеру отдельно. Они пишут пост сами — вы согласуете перед публикацией.
        </div>
      </Card>
    </div>
  );
}

function BriefNameField({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const [editing, setEditing] = useState(false);
  const ref = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (editing) ref.current?.select();
  }, [editing]);

  return editing ? (
    <input
      ref={ref}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      onBlur={() => setEditing(false)}
      onKeyDown={(e) => e.key === "Enter" && setEditing(false)}
      className="h-8 px-2 rounded-md border border-ink/40 bg-white font-display font-bold text-[15px] outline-none w-48"
    />
  ) : (
    <button
      onClick={() => setEditing(true)}
      className="text-left font-display font-bold text-[15px] text-ink hover:text-ink-60 transition-colors"
      title="Переименовать вариант"
    >
      {value} <span className="text-[11px] text-ink-30 font-normal ml-1">переименовать</span>
    </button>
  );
}

type BriefMedia = { id: string; kind: "image" | "video"; url: string };

function BriefMediaBlock({
  media,
  onChange,
}: {
  media: BriefMedia[];
  onChange: (m: BriefMedia[]) => void;
}) {
  const fileRef = useRef<HTMLInputElement>(null);
  const upload = useUploadMedia();
  const [dragId, setDragId] = useState<string | null>(null);

  const mediaRef = useRef(media);
  mediaRef.current = media;
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  async function onFiles(files: FileList | null) {
    if (!files) return;
    const results: BriefMedia[] = [...mediaRef.current];
    for (const f of Array.from(files)) {
      const isVideo = f.type.startsWith("video/");
      try {
        const out = await upload.mutateAsync({ file: f, kind: isVideo ? "video" : "image" });
        results.push({ id: out.id, kind: out.kind as "image" | "video", url: out.url });
        onChangeRef.current([...results]);
      } catch (e) {
        alert(`Не удалось загрузить ${f.name}: ${e instanceof Error ? e.message : "ошибка"}`);
      }
    }
  }

  function onDragStart(e: DragEvent<HTMLDivElement>, id: string) {
    setDragId(id);
    e.dataTransfer.effectAllowed = "move";
  }
  function onDragOver(e: DragEvent<HTMLDivElement>) { e.preventDefault(); }
  function onDrop(e: DragEvent<HTMLDivElement>, targetId: string) {
    e.preventDefault();
    if (!dragId || dragId === targetId) return;
    const src = media.findIndex((m) => m.id === dragId);
    const dst = media.findIndex((m) => m.id === targetId);
    if (src < 0 || dst < 0) return;
    const next = [...media];
    const [moved] = next.splice(src, 1);
    next.splice(dst, 0, moved);
    onChange(next);
    setDragId(null);
  }

  return (
    <div className="grid gap-2">
      <div className="text-[11px] font-display font-bold uppercase tracking-[0.07em] text-ink-40">
        Материалы для блогера
        <span className="ml-1.5 font-normal normal-case tracking-normal text-ink-30">фото или видео-референсы</span>
      </div>
      <div className="flex gap-2 flex-wrap">
        {media.map((m) => (
          <div
            key={m.id}
            draggable
            onDragStart={(e) => onDragStart(e, m.id)}
            onDragOver={onDragOver}
            onDrop={(e) => onDrop(e, m.id)}
            className={cn(
              "relative w-20 h-20 rounded-md overflow-hidden bg-ink-6 group flex-shrink-0 cursor-move",
              dragId === m.id && "opacity-50",
            )}
            title="Перетащите чтобы изменить порядок"
          >
            {m.kind === "video" ? (
              <div className="grid place-items-center w-full h-full text-ink-40 text-[11px] font-display font-semibold">
                ▶ видео
              </div>
            ) : (
              <img src={m.url} alt="" className="w-full h-full object-cover" />
            )}
            <button
              onClick={() => onChange(media.filter((x) => x.id !== m.id))}
              className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-ink text-white grid place-items-center opacity-0 group-hover:opacity-100 transition-opacity"
            >
              <Trash2 size={10} />
            </button>
            <div className="absolute top-1 left-1 text-ink-40 opacity-0 group-hover:opacity-80">
              <GripVertical size={12} />
            </div>
          </div>
        ))}
        <button
          onClick={() => fileRef.current?.click()}
          disabled={upload.isPending}
          className={cn(
            "w-20 h-20 grid place-items-center rounded-md border-2 border-dashed border-ink-12 text-ink-40 hover:border-ink hover:text-ink transition-colors flex-shrink-0 flex flex-col gap-1",
            upload.isPending && "opacity-50 cursor-not-allowed",
          )}
        >
          {upload.isPending ? (
            <span className="text-[11px] font-display font-semibold">…</span>
          ) : (
            <>
              <ImagePlus size={18} strokeWidth={1.5} />
              <span className="text-[10px] font-display font-semibold">Загрузить</span>
            </>
          )}
        </button>
      </div>
      <input
        ref={fileRef}
        type="file"
        accept="image/png,image/jpeg,image/webp,image/gif,video/mp4,video/webm,video/quicktime"
        multiple
        hidden
        onChange={(e) => { onFiles(e.target.files); e.target.value = ""; }}
      />
    </div>
  );
}

function BriefSection({
  label, hint, children,
}: {
  label: string; hint?: string; children: React.ReactNode;
}) {
  return (
    <div className="grid gap-1.5">
      <div className="text-[11px] font-display font-bold uppercase tracking-[0.07em] text-ink-40">{label}</div>
      {hint && <div className="text-[12px] text-ink-40 -mt-0.5">{hint}</div>}
      {children}
    </div>
  );
}

function BriefTextarea({ value, onChange, rows, placeholder }: {
  value: string; onChange: (v: string) => void; rows?: number; placeholder?: string;
}) {
  return (
    <textarea
      value={value}
      onChange={(e) => onChange(e.target.value)}
      rows={rows ?? 3}
      placeholder={placeholder}
      className="p-3 rounded-md border border-ink-12 bg-white text-[14px] leading-relaxed outline-none focus:border-ink resize-none placeholder:text-ink-40"
    />
  );
}

function BriefInput({ value, onChange, placeholder, type, mono }: {
  value: string; onChange: (v: string) => void; placeholder?: string; type?: string; mono?: boolean;
}) {
  return (
    <input
      type={type ?? "text"}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className={cn(
        "h-10 px-3 rounded-md border border-ink-12 bg-white text-[13.5px] outline-none focus:border-ink placeholder:text-ink-40 w-full",
        mono && "font-mono text-[12px]",
      )}
    />
  );
}
